import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PaymentMethodCard = ({ user, onUpdatePaymentMethod }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  // Mock payment method data
  const paymentMethod = user?.isPremium ? {
    type: 'card',
    brand: 'visa',
    last4: '4242',
    expiryMonth: 12,
    expiryYear: 2027,
    isDefault: true
  } : null;

  const handleUpdatePaymentMethod = async () => {
    setIsUpdating(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsUpdating(false);
    onUpdatePaymentMethod?.();
  };

  const getCardIcon = (brand) => {
    const icons = {
      visa: 'CreditCard',
      mastercard: 'CreditCard',
      amex: 'CreditCard',
      discover: 'CreditCard'
    };
    return icons?.[brand] || 'CreditCard';
  };

  const getCardBrandName = (brand) => {
    const names = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover'
    };
    return names?.[brand] || 'Card';
  };

  if (!user?.isPremium) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
            <Icon name="CreditCard" size={20} className="text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Payment Method</h3>
            <p className="text-sm text-muted-foreground">No payment method on file</p>
          </div>
        </div>
        
        <div className="text-center py-1">
          <p className="text-muted-foreground text-sm mb-4">
            Add a payment method when you upgrade to Premium
          </p>
          <Button variant="outline" iconName="Plus" iconPosition="left">
            Add Payment Method
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Payment Method</h3>
            <p className="text-muted-foreground text-sm">
              Manage your billing information
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            loading={isUpdating}
            onClick={handleUpdatePaymentMethod}
            iconName="Edit" 
            iconPosition="left"
          >
            Update
          </Button>
        </div>
      </div>
      <div className="p-6">
        {paymentMethod ? (
          <div className="space-y-4">
            {/* Current Payment Method */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-8 bg-primary/10 rounded flex items-center justify-center">
                  <Icon name={getCardIcon(paymentMethod?.brand)} size={20} className="text-primary" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-foreground">
                      {getCardBrandName(paymentMethod?.brand)} •••• {paymentMethod?.last4}
                    </span>
                    {paymentMethod?.isDefault && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-success/10 text-success">
                        <Icon name="Check" size={10} className="mr-1" />
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Expires {paymentMethod?.expiryMonth?.toString()?.padStart(2, '0')}/{paymentMethod?.expiryYear}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm" iconName="Edit">
                  Edit
                </Button>
                <Button variant="ghost" size="sm" iconName="Trash2">
                  Remove
                </Button>
              </div>
            </div>

            {/* Security Information */}
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Shield" size={20} className="text-success mt-0.5" />
                <div>
                  <h4 className="font-medium text-foreground mb-1">Secure Payment Processing</h4>
                  <p className="text-sm text-muted-foreground">
                    Your payment information is encrypted and securely processed by Stripe. 
                    We never store your full card details on our servers.
                  </p>
                </div>
              </div>
            </div>

            {/* Add Another Payment Method */}
            <div className="pt-4 border-t border-border">
              <Button 
                variant="outline" 
                fullWidth 
                iconName="Plus" 
                iconPosition="left"
              >
                Add Another Payment Method
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="CreditCard" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Payment Method</h4>
            <p className="text-muted-foreground mb-6">
              Add a payment method to manage your subscription
            </p>
            <Button variant="default" iconName="Plus" iconPosition="left">
              Add Payment Method
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodCard;